/**
 * "How to read this table" — prototype screen 3a table-key popover.
 * Visual contract: artifacts/15/design-refs/figabl-eve/prototype/assets/render.js tableKeyPopover()
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import './variantMappingDesign.css';

function KeyRow({ leading, children }: { leading: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="vm-design-pop__keyrow">
      <div className="vm-design-pop__lead">{leading}</div>
      <div className="vm-design-pop__text">{children}</div>
    </div>
  );
}

const VariantMappingTableKey: React.FC = () => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="vm-design__info-anchor" ref={rootRef}>
      <button
        type="button"
        className="vm-design__info-btn"
        title="How to read this table"
        aria-label="How to read this table"
        aria-expanded={open}
        aria-controls={open ? 'variant-mapping-table-key-popover' : undefined}
        data-testid="variant-mapping-table-key"
        onClick={() => setOpen((current) => !current)}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
          <path d="M12 11.2v5.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <circle cx="12" cy="7.9" r="1" fill="currentColor" />
        </svg>
      </button>
      {open && (
        <div
          id="variant-mapping-table-key-popover"
          className="vm-design-pop"
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          data-testid="variant-mapping-table-key-popover"
        >
          <div className="vm-design-pop__head">
            <h2 id={titleId} className="vm-design-pop__title">
              How to read this table
            </h2>
            <button
              type="button"
              className="vm-design-pop__close"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>
          <div className="vm-design-pop__body">
            <KeyRow leading={<div className="vm-design-pop__swatch vm-design-pop__swatch--sold" />}>
              <strong>Sold as</strong> — the name this set carries in every table, report and export.
            </KeyRow>
            <KeyRow leading={<div className="vm-design-pop__swatch vm-design-pop__swatch--bought" />}>
              <strong>Bought as</strong> — the name used on purchase orders. It can be the same cell.
            </KeyRow>
            <KeyRow leading={<div className="vm-design-pop__swatch vm-design-pop__swatch--attn" />}>
              <strong>Red outline</strong> — to display correctly there should be data in this cell. Either
              map a variant here, or change the Displayed as for this row.
            </KeyRow>
            <div className="vm-design-pop__rule" />
            <KeyRow
              leading={<span className="vm-design-pop__count vm-design-pop__count--bought">1,200</span>}
            >
              Units <strong>bought</strong>, top right — from inventory data.
            </KeyRow>
            <KeyRow
              leading={<span className="vm-design-pop__count vm-design-pop__count--sold">964</span>}
            >
              Units <strong>sold</strong>, bottom right — from sales transactions. Blank means no movement.
            </KeyRow>
            <div className="vm-design-pop__rule" />
            <p className="vm-design-pop__text vm-design-pop__text--lede">
              Clicking a cell makes it <strong>Sold as</strong>. A row&apos;s ⋯ menu has{' '}
              <strong>Override &quot;Sold As&quot; Variant</strong> and{' '}
              <strong>Override &quot;Bought As&quot; Variant</strong> for picking either precisely.{' '}
              <strong>Set defaults</strong> chooses the column every set uses.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantMappingTableKey;
